import React, { FC, useState } from "react";
import classNames from "classnames";
import { Card } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RouteComponentProps, useHistory } from "react-router";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import AccessTokenSessionService from "go-security/services/accessTokenSessionService";
import { selectOrganization } from "go-security/services/organizations/selectors";
import {
	ApplicationApi,
	ApplicationTemplateApi,
	ApplicationUpdateApi,
} from "../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../services/Api/api";
import { Providers } from "../../services/types";
import AppCardInstalled from "./AppCardInstalled";
import AppCardUninstalled from "./AppCardUninstalled";
import AppModal from "./AppModal";
import GastroSupplierInstallationModal from "./GastroSupplier/GastroSupplierInstallationModal";
import MultiLocationInstallModal from "./MultiLocation/MultiLocationInstallModal";

interface Props {
	configApp: ApplicationTemplateApi;
	installedApp?: ApplicationApi;
	routeProps?: RouteComponentProps;
	handleUninstall?: (app: ApplicationApi) => void;
	handleUpdate?: (app: ApplicationApi) => void;
	handleUpdateNewInstalledApp?: (app: ApplicationApi) => void;
}

const AppCard: FC<Props> = ({
	configApp,
	installedApp,
	routeProps,
	handleUpdate,
	handleUninstall,
	handleUpdateNewInstalledApp,
}) => {
	const [showModal, setShowModal] = useState(false);
	const [showLocationsModal, setShowLocationsModal] = useState(false);
	const [showGastroSupplierModal, setShowGastroSupplierModal] = useState(false);
	const [showGastroSupplierMerchantModal, setShowGastroSupplierMerchantModal] = useState(false);
	const history = useHistory();
	const { addFlash, addSuccessFlash } = useFlash();
	const organization = useSelector(selectOrganization);
	const { t } = useTranslation();

	const handleInstall = async () => {
		const accessToken = AccessTokenSessionService.get();
		try {
			switch (configApp.provider) {
				case Providers.LOCATIONS:
					setShowLocationsModal(true);
					break;
				case Providers.GASTROSUPPLIER:
					setShowGastroSupplierModal(true);
					break;
				case Providers.DOCUMENT_TRANSFER: {
					const res = await api
						.organization()
						.installApps(Providers.DOCUMENT_TRANSFER, {} as ApplicationUpdateApi);
					addSuccessFlash(t("lib:common.flash.completed"));
					history.push(`/${organization.id}/apps/active/${res.id}`);
					break;
				}
				case Providers.DOCUMENT_FILE_IMPORT: {
					const res = await api
						.organization()
						.installApps(Providers.DOCUMENT_FILE_IMPORT, {} as ApplicationUpdateApi);
					addSuccessFlash(t("lib:common.flash.completed"));
					history.push(`/${organization.id}/apps/active/${res.id}`);
					break;
				}
				case Providers.GASTROSUPPLIER_MERCHANT:
					setShowGastroSupplierMerchantModal(true);
					break;
				default:
					api.organization().installApplication(configApp.provider, accessToken ? accessToken : "");
			}
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	return (
		<div className={"col-md-4 mb-3"}>
			{showModal && (
				<AppModal
					configApp={configApp}
					key={showModal ? "modal-on" : "modal-off"}
					installedApp={installedApp}
					onHide={() => {
						setShowModal(false);
						setShowLocationsModal(false);
						setShowGastroSupplierModal(false);
						setShowGastroSupplierMerchantModal(false);
						setShowGastroSupplierModal(false);
					}}
				/>
			)}
			{showLocationsModal && <MultiLocationInstallModal onHide={() => setShowLocationsModal(false)} />}
			{showGastroSupplierModal && (
				<GastroSupplierInstallationModal
					onHide={() => setShowGastroSupplierModal(false)}
					show={showGastroSupplierModal}
					provider={Providers.GASTROSUPPLIER}
				/>
			)}
			{showGastroSupplierMerchantModal && (
				<GastroSupplierInstallationModal
					onHide={() => setShowGastroSupplierMerchantModal(false)}
					show={showGastroSupplierMerchantModal}
					provider={Providers.GASTROSUPPLIER_MERCHANT}
				/>
			)}
			<Card
				className={classNames("h-100", { "card-hover": true })}
				onClick={() =>
					installedApp ? history.push(`${routeProps?.match.url}/${installedApp.id}`) : setShowModal(true)
				}
			>
				<Card.Body>
					{installedApp ? (
						<AppCardInstalled
							handleUninstall={handleUninstall}
							routeProps={routeProps}
							installedApp={installedApp}
							configApp={configApp}
							handleUpdate={handleUpdate}
						/>
					) : (
						<AppCardUninstalled configApp={configApp} onInstall={handleInstall} />
					)}
				</Card.Body>
			</Card>
		</div>
	);
};
export default AppCard;
